/**
 * Moda Warsaw — cart drawer cross-sell custom element.
 *
 * Loaded unconditionally as a module script from snippets/cart-drawer.liquid
 * (the same pattern Horizon itself uses to load cart-drawer.js), so
 * `customElements.define` runs on every real page load regardless of
 * whether the cart is empty at that moment.
 *
 * This matters because the <lm-cart-cross-sell> element itself only gets
 * rendered once the cart is non-empty, and on a fresh session that first
 * happens via Horizon's cart-drawer morph (AJAX add-to-cart), not a full
 * page navigation. Browsers never execute an inline <script> that arrives
 * through an innerHTML-based DOM update, only through the initial HTML
 * parse — so defining the element here, in a script that's always part of
 * the initial page load, is what lets the element upgrade the moment it is
 * inserted (browsers auto-upgrade any element matching a registered custom
 * element tag name as soon as it's connected, including via morph/innerHTML).
 */
if (!customElements.get('lm-cart-cross-sell')) {
  customElements.define(
    'lm-cart-cross-sell',
    class extends HTMLElement {
      connectedCallback() {
        this.base = (this.dataset.root || '/').replace(/\/$/, '');
        this.requestId = 0;
        this.addEventListener('click', this.onClick);
        // Listened for on document, not on self: the event is dispatched by
        // other components (line item quantity, remove, other quick-adds)
        // that are not inside this element.
        document.addEventListener('cart:update', this.onCartUpdate);
        this.load();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('cart:update', this.onCartUpdate);
      }

      onCartUpdate = async (event) => {
        // The element is excluded from morphing, so the server no longer
        // refreshes `data-product-id`. Re-seed on the newest cart line from
        // the cart the event carries, and only read it back if it did not.
        let cart = event.detail && event.detail.resource;
        if (!cart || !Array.isArray(cart.items)) {
          try {
            cart = await (await fetch(this.base + '/cart.js')).json();
          } catch (error) {
            return;
          }
        }

        const items = cart.items || [];
        if (!items.length) return;

        this.dataset.productId = items[items.length - 1].product_id;
        this.load();
      };

      async load() {
        const productId = this.dataset.productId;
        if (!productId) return;

        // Cart changes can arrive faster than the endpoint answers. Stamp
        // each run so a slow earlier response cannot paint over a newer one.
        const requestId = ++this.requestId;

        // Merchandised pairings first, algorithmic ones only if there are none.
        const markup =
          (await this.fetchSection(productId, 'complementary')) ||
          (await this.fetchSection(productId, 'related'));

        if (requestId !== this.requestId) return;

        // Nothing left to recommend — hide rather than leave the previous,
        // now-stale row sitting there.
        if (!markup) {
          this.innerHTML = '';
          this.hidden = true;
          return;
        }

        this.innerHTML = markup;
        this.hidden = false;
      }

      async fetchSection(productId, intent) {
        const url =
          this.base +
          // 10 is the endpoint's maximum. The section shows three, but it
          // discards anything of the wrong gender first, so it needs more
          // than three to choose from.
          '/recommendations/products?section_id=lm-cart-upsell&limit=10&product_id=' +
          encodeURIComponent(productId) +
          '&intent=' +
          intent;

        try {
          const response = await fetch(url);
          if (!response.ok) return null;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-lm-cart-upsell-root]');
          return root ? root.outerHTML : null;
        } catch (error) {
          // A missing recommendation is not worth breaking the drawer over.
          return null;
        }
      }

      onClick = async (event) => {
        const button = event.target.closest('button[data-lm-cart-add]');
        if (!button) return;

        // Single-variant products carry the id on the button; everything
        // else reads it off the size select sitting next to it.
        const row = button.closest('.lm-cart-upsell__item');
        const select = row && row.querySelector('[data-lm-cart-size]');
        const variantId = Number(button.dataset.variantId || (select && select.value));
        if (!variantId) return;

        button.setAttribute('aria-busy', 'true');

        try {
          // Ask for the cart-drawer-section's fresh markup in the same
          // request (a documented AJAX Cart API parameter), then swap it
          // into the live <cart-items-component> so the item list, subtotal
          // and cart count all refresh instead of going stale the way a bare
          // cart.js re-fetch would. A plain innerHTML swap of the component's
          // contents (rather than replacing the <dialog>/<theme-drawer>
          // wrapper around it) keeps the drawer open.
          const response = await fetch(this.base + '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: 'cart-drawer-section',
            }),
          });
          const result = await response.json();
          const sectionHtml = result.sections && result.sections['cart-drawer-section'];

          // Tag the cart so real conversions from this drawer upsell can be
          // measured later (order.customAttributes carries this through to
          // checkout) -- same convention already used on annoboutique
          // (_ab_cross_sell_cart) for exactly this question.
          fetch(this.base + '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _lm_cross_sell_cart: 'cart-drawer' } }),
          }).catch(() => {});

          if (sectionHtml) {
            const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const newComponent = parsed.querySelector('cart-items-component');
            const oldComponent = document.querySelector('cart-items-component');

            if (newComponent && oldComponent) {
              oldComponent.innerHTML = newComponent.innerHTML;
            }
          }

          const cart = await (await fetch(this.base + '/cart.js')).json();

          // The header cart icon's count bubble lives outside
          // cart-drawer-section (it's part of the header sections), so the
          // swap above doesn't touch it. Patch its text directly rather than
          // re-fetching and swapping a second section for one number.
          document.querySelectorAll('.cart-bubble, .theme-drawer__badge').forEach((el) => {
            el.textContent = String(cart.item_count);
          });

          // Lightweight signal for this row's own refresh (new seed, cart
          // filtering) — the swap above already handled the item list and
          // totals.
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'lm-cart-upsell',
                data: { itemCount: cart.item_count, source: 'lm-cart-upsell' },
              },
            })
          );
        } catch (error) {
          button.removeAttribute('aria-busy');
        }
      };
    }
  );
}

